import dayjs from "dayjs";

interface ComputeEffectiveDateRangeParams {
  startOffset?: number;
  duration?: number;
  staticStart?: Date;
  staticEnd?: Date;
  requireEnd: boolean;
  allowSameDayEnd?: boolean;
}

/**
 * Computes an effective date range from either offset-based or static date inputs.
 * Offset-based dates take priority over static dates when both are provided.
 */
export function computeEffectiveDateRange(
  input: ComputeEffectiveDateRangeParams,
): { start: Date; end: Date | undefined } {
  const {
    startOffset,
    duration,
    staticStart,
    staticEnd,
    requireEnd,
    allowSameDayEnd,
  } = input;

  const hasStartOffset = startOffset !== undefined;
  const hasStaticStart = !hasStartOffset && staticStart !== undefined;

  let effectiveStart: dayjs.Dayjs | null = null;
  let effectiveEnd: dayjs.Dayjs | null = null;

  if (hasStartOffset) {
    effectiveStart = dayjs().add(startOffset, "day");
    if (duration !== undefined) {
      effectiveEnd = effectiveStart.add(duration, "day");
    }
  } else if (hasStaticStart) {
    effectiveStart = dayjs(staticStart);
    if (staticEnd !== undefined) {
      effectiveEnd = dayjs(staticEnd);
    }
  }

  if (!effectiveStart) {
    throw new Error(
      "Either a start offset or a static start date must be provided to compute the effective date range",
    );
  }

  if (effectiveStart.isBefore(dayjs(), "day")) {
    throw new Error("Start date cannot be in the past");
  }

  if (requireEnd) {
    if (!effectiveEnd) {
      throw new Error(
        "Either a duration or a static end date must be provided to compute the effective end date",
      );
    }

    if (effectiveEnd.isBefore(effectiveStart, "day")) {
      throw new Error("End date must not be before start date");
    }

    if (!allowSameDayEnd && effectiveEnd.isSame(effectiveStart, "day")) {
      throw new Error("End date must not be the same as start date");
    }
  }

  return {
    start: effectiveStart.toDate(),
    end: requireEnd && effectiveEnd ? effectiveEnd.toDate() : undefined,
  };
}
